import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft, Mail } from 'lucide-react';
import axios from 'axios';
import { useSeo, SEO_KEYWORDS } from '../hooks/useSeo';

const ForgotPasswordPage = () => {
  useSeo({ title: 'Reset Your Password | InvestiGate', description: 'Request a secure password reset link for your InvestiGate account.', keywords: SEO_KEYWORDS, noindex: true });
  const [email, setEmail] = useState('');
  const [state, setState] = useState('idle'); // idle | loading | success | error
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setState('loading');
    setError('');
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`,
        { email: email.trim() },
        { withCredentials: true }
      );
      setState('success');
    } catch (err) {
      setState('error');
      setError('We could not process that request. Please try again later.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <Shield className="w-12 h-12 text-blue-600" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
          {state === 'success' ? 'Check your email' : 'Forgot password?'}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          {state === 'success'
            ? 'Follow the reset link in your inbox to choose a new password.'
            : "We'll email you a link to reset your password."}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {state === 'success' ? (
            <div className="text-center space-y-4" data-testid="forgot-success">
              <div className="mx-auto w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center">
                <Mail className="w-7 h-7 text-blue-600" />
              </div>
              <p className="text-sm text-gray-700">
                If an account exists for <span className="font-medium">{email}</span>, a reset link is on its way.
              </p>
              <p className="text-xs text-gray-500">
                The link expires in 30 minutes. Check your spam folder if you don&apos;t see it.
              </p>
              <div className="pt-4">
                <Link
                  to="/login"
                  className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-500"
                  data-testid="back-to-login-link"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back to login
                </Link>
              </div>
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit} noValidate>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  data-testid="forgot-email-input"
                />
              </div>

              {state === 'error' && (
                <p className="text-sm text-red-600" role="alert" data-testid="forgot-error">
                  {error}
                </p>
              )}

              <div>
                <button
                  type="submit"
                  disabled={state === 'loading' || !email.trim()}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  data-testid="forgot-submit-btn"
                >
                  {state === 'loading' ? 'Sending…' : 'Send reset link'}
                </button>
              </div>

              <div className="text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-500"
                  data-testid="forgot-back-link"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back to login
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
